import { reconcileBasicInfoGraph } from 'views/complex-onboarding-flow/reconcile-basic-info';
import { STEP_FALLBACK, shortStep } from 'views/sessions/deck';
import type { IStep } from 'views/design-system';

/** One line of a flow's step chain: an optional branch chip, then the steps. */
export interface IChainRow {
	key: string;
	/** the condition that leads down this line — `individual`, `business`, … */
	branch?: string;
	steps: IStep[];
}

/**
 * Branch labels live in the edge's `sourceHandle`, not in a label field.
 *
 * A plain continuation edge carries `sourceHandle === source` (the node's own
 * id). A conditional edge carries `<sourceId>_multipleChoice_Individual`, so the
 * condition is whatever follows the source id — with the `multipleChoice_`
 * marker dropped, since it names the widget rather than the branch.
 */
export const branchLabel = (edge: any, index: number): string | undefined => {
	const handle: string = edge?.sourceHandle ?? '';
	const source: string = edge?.source ?? '';
	if (edge?.label) return String(edge.label);
	if (!handle || handle === source) return undefined;
	const rest = handle.startsWith(`${source}_`)
		? handle.slice(source.length + 1)
		: handle.startsWith(source)
		? handle.slice(source.length)
		: handle;
	const cleaned = rest.replace(/^multipleChoice_/, '').replace(/[_-]+/g, ' ').trim();
	return cleaned ? cleaned.toLowerCase() : `branch ${index + 1}`;
};

const ACCREDITATION_BRANCH: Record<string, string> = {
	'506b': '506b',
	'506c': '506c',
	investorChoice: 'investor choice',
};

/**
 * A flow's steps as chain rows.
 *
 * Straight-line flows come back as one row. Where the graph fans out, the trunk
 * is the first row and every out-edge of the fan-out starts its own row, chipped
 * with the condition that reaches it — which is how a branching flow stays
 * readable as text instead of needing the canvas.
 *
 * Source of truth is `design.nodes`/`design.edges` through
 * `reconcileBasicInfoGraph` (the same reconciliation the graph preview uses, so
 * a legacy tier-0 Basic Info node shows here too). A flow with no design blob
 * falls back to `fullStatus` in payload order.
 */
export const toChainRows = (flow: any): IChainRow[] => {
	const { nodes, edges } = reconcileBasicInfoGraph(
		flow?.design?.nodes ?? [],
		flow?.design?.edges ?? [],
		flow?.nodes ?? []
	);

	/* THE TIER IS A PROPERTY OF THE NODE, NOT OF THE FLOW.
	   This used to take the FIRST accreditation key in `fullStatus` and stamp it
	   on every accreditation pill, so a flow built 506(b) -> 506(c) listed as
	   506b -> 506b while the canvas, the publish payload and the stored node all
	   said 506c. The list endpoint already answers per node: pipelines.api sets
	   `node.type` from `actions[0].metadata.accreditationType.value` on each one
	   (complex-flow `find`), and `flow.nodes[]._id` is the id `design.nodes[]`
	   reference. Read it there; the flow-level pick stays only as the fallback
	   for a payload with no node detail. */
	const tierByNode = new Map<string, string>();
	(flow?.nodes ?? []).forEach((n: any) => {
		if (n?.stepId !== 'accreditation') return;
		const meta = n?.actions?.[0]?.metadata?.accreditationType;
		const raw = n?.type ?? (typeof meta === 'string' ? meta : meta?.value);
		const tier = ACCREDITATION_BRANCH[raw];
		if (!tier) return;
		[n?._id, n?.id].forEach(id => id && tierByNode.set(String(id), tier));
	});
	const fallbackTier = (flow?.fullStatus ?? []).find(
		(k: string) => ACCREDITATION_BRANCH[k]
	);
	const tierOf = (node: any): string | undefined =>
		tierByNode.get(String(node?.id)) ??
		tierByNode.get(String(node?._id)) ??
		(fallbackTier ? ACCREDITATION_BRANCH[fallbackTier] : undefined);

	if (!nodes?.length) {
		const steps: IStep[] = (flow?.fullStatus ?? []).map((key: string, i: number) => ({
			key: `${key}-${i}`,
			label: shortStep(STEP_FALLBACK[key] ?? key),
			state: 'pending' as const,
		}));
		return steps.length ? [{ key: 'trunk', steps }] : [];
	}

	const byId = new Map<string, any>(nodes.map((n: any) => [n.id, n]));
	const out = new Map<string, any[]>();
	const targets = new Set<string>();
	(edges ?? []).forEach((e: any) => {
		if (!byId.has(e.source) || !byId.has(e.target)) return;
		out.set(e.source, [...(out.get(e.source) ?? []), e]);
		targets.add(e.target);
	});

	const toStep = (node: any): IStep => ({
		key: node.id,
		label: shortStep(
			node.label ?? STEP_FALLBACK[node.stepId] ?? node.stepId ?? node.key ?? 'step'
		),
		state: 'pending',
		// The accreditation tier qualifies the pill, so it rides on it rather than
		// opening a row of its own -- and it is looked up per node (see tierOf).
		branch: node.stepId === 'accreditation' ? tierOf(node) : undefined,
	});

	const rows: IChainRow[] = [];
	const seen = new Set<string>();

	/** Walk one line until it ends or forks; every fork spawns another row. */
	const walk = (startId: string, branch: string | undefined, rowKey: string) => {
		const steps: IStep[] = [];
		let id: string | undefined = startId;
		while (id && byId.has(id) && !seen.has(id)) {
			seen.add(id);
			steps.push(toStep(byId.get(id)));
			const next: any[] = out.get(id) ?? [];
			if (next.length === 1) {
				id = next[0].target;
				continue;
			}
			if (next.length > 1) {
				// Emit this line first so branch rows read underneath their trunk.
				if (steps.length) rows.push({ key: rowKey, branch, steps: [...steps] });
				steps.length = 0;
				next.forEach((edge, i) =>
					walk(edge.target, branchLabel(edge, i), `${rowKey}-${i}`)
				);
				return;
			}
			id = undefined;
		}
		if (steps.length) rows.push({ key: rowKey, branch, steps });
	};

	const roots = nodes.filter((n: any) => !targets.has(n.id));
	(roots.length ? roots : [nodes[0]]).forEach((root: any, i: number) =>
		walk(root.id, undefined, `r${i}`)
	);
	// Anything unreachable from a root still deserves to be shown.
	nodes.forEach((n: any, i: number) => !seen.has(n.id) && walk(n.id, undefined, `o${i}`));

	return rows;
};

/** `authentication+form+kyc+…` — the step-key line under a chain. */
export const stepKeyLine = (flow: any) => (flow?.fullStatus ?? []).join('+');

/** A flow contains a Fund (Pay Out) step, which bars QR generation. */
export const hasPayOut = (flow: any) =>
	(flow?.nodes ?? []).some((n: Record<string, string>) => n.type === 'payOut');
